import { StackSpace } from '@grubhubprod/gh-cookbook-react';
import { uniqBy } from '@grubhubprod/umami-library/dist/nodash/uniqBy';
import { useEffect, useRef, useState } from 'react';
import { CSV_UPLOAD_ERROR_MESSAGES } from 'src/components/CSVUploadTracking/CsvUploadErrorMessages';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { sdk } from 'src/utilities/sdk';

export const UPLOAD_STATUS_INTERVAL = 200;
const MAX_POSSIBLE_ERRORS = 300;
const MAX_UPLOAD_RETRIES = 4;

export interface UploadStatus {
  status: CSVUploadStatus;
  progress: number;
  lineErrors: UploadError[];
  fileErrors: UploadError[];
  hasError: boolean;
  exceedsMaxErrors: boolean;
  isSuccessful: boolean;
  totalRecords: number;
}

export interface UseCSVUpload {
  currentUploadStatus: UploadStatus;
  reset: () => void;
  startCsvUpload: (csvFile: File) => Promise<void>;
  isUploading: boolean;
}

export enum CSVUploadStatus {
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
  NEW = 'NEW',
  IN_PROGRESS = 'IN_PROGRESS',
  CANCELLED = 'CANCELLED',
  NOT_FOUND = 'NOT_FOUND',
  FILE_ERROR = 'FILE_ERROR',
}

export interface CsvUploadStatusResponse {
  owner_id: string;
  request_id: string;
  status: string;
  type: string;
  diners_created: number;
  diners_updated: number;
  diners_skipped: number;
  diners_deactivated: number;
  total_records: number;
  total_processed: number;
  error_count: number;
  date_created: string;
  date_updated: string;
  file_name: string;
  owner_name: string;
  request_user_id?: string;
}

export interface UploadError {
  id: string;
  line?: number;
  message: string;
}

export interface UploadErrorResponse {
  id: string;
  line_number: string;
  property: string;
  message_key: string;
}

export const defaultUploadStatus: UploadStatus = {
  status: null,
  progress: 1, // start bar with a little progress
  lineErrors: [],
  fileErrors: [],
  hasError: false,
  exceedsMaxErrors: false,
  isSuccessful: false,
  totalRecords: 0,
};
export const useCSVUpload = (corpId: string): UseCSVUpload => {
  const uploadRequestId = useRef(null);
  const uploadInterval = useRef(null);
  const acceptedCsvFile = useRef<File>(null);
  const uploadPendingRequest = useRef(null);
  const uploadRetries = useRef(0);
  const [currentUploadStatus, setCurrentUploadStatus] =
    useState(defaultUploadStatus);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    return clearUploadInterval;
  }, []);

  function clearUploadInterval() {
    if (uploadInterval.current) {
      clearInterval(uploadInterval.current);
    }
  }

  async function startCsvUpload(csvFile: File) {
    setIsUploading(true);
    acceptedCsvFile.current = csvFile;
    try {
      const uploadUrlResponse = await sdk.http.post(
        `/corp-admin/${corpId}/diners/upload-url`,
        {}
      );

      await uploadCsvToS3(uploadUrlResponse);
    } catch (error) {
      retryOnError();
    }
  }

  function retryOnError() {
    uploadPendingRequest.current = false;
    clearUploadInterval();
    retryUploadProcess();
  }

  function retryUploadProcess() {
    if (uploadRetries.current < MAX_UPLOAD_RETRIES) {
      uploadRetries.current += 1;
      startCsvUpload(acceptedCsvFile.current);
    } else {
      uploadRetries.current = 0;
      updateCurrentUploadStatus({
        status: CSVUploadStatus.ERROR,
        hasError: true,
      });
    }
  }

  function updateCurrentUploadStatus(statusProps: Partial<UploadStatus>) {
    const currentUploadStatus = {
      ...defaultUploadStatus,
      ...statusProps,
    };
    setCurrentUploadStatus(currentUploadStatus);
  }
  function reset() {
    updateCurrentUploadStatus(defaultUploadStatus);
  }

  async function uploadCsvToS3(uploadUrlResponse) {
    try {
      uploadRequestId.current = uploadUrlResponse.id;

      // Simple PUT request to Amazon S3 bucket by skipping auth and any extra header value
      const headers = new Headers();
      headers.append('Content-Type', 'multipart/form-data');
      await fetch(uploadUrlResponse.uri, {
        method: 'PUT',
        body: acceptedCsvFile.current,
        headers,
      });

      await handleS3UploadResponseSuccess();
    } catch (error) {
      retryOnError();
    }
  }

  async function handleS3UploadResponseSuccess() {
    try {
      await sdk.http.post(
        `/corp-admin/${corpId}/diners/upload/${uploadRequestId.current}`,
        {}
      );

      getProcessingUploadStatus();
    } catch (error) {
      showGeneralErrorMessage(error);
      updateCurrentUploadStatus({
        status: CSVUploadStatus.ERROR,
        hasError: true,
      });
    }
  }

  function getProcessingUploadStatus() {
    uploadInterval.current = setInterval(async () => {
      try {
        if (!uploadPendingRequest.current) {
          uploadPendingRequest.current = true;
          const uploadStatus: CsvUploadStatusResponse = await sdk.http.get(
            `/corp-admin/${corpId}/diners/upload/${uploadRequestId.current}`
          );
          handleUploadStatusResponse(uploadStatus);
        }
      } catch (error) {
        showGeneralErrorMessage(error);
      }
    }, UPLOAD_STATUS_INTERVAL);
  }

  function showGeneralErrorMessage(error) {
    clearInterval(undefined);
    pushErrorFromAPI(error);
  }

  function handleUploadStatusResponse(uploadStatus: CsvUploadStatusResponse) {
    uploadRequestId.current = uploadStatus.request_id;
    uploadPendingRequest.current = false;

    switch (uploadStatus.status) {
      case CSVUploadStatus.COMPLETE:
        updateCurrentUploadStatus({
          status: CSVUploadStatus.COMPLETE,
          progress: 100,
          isSuccessful: true,
          totalRecords: uploadStatus.total_records,
        });

        clearUploadInterval();
        setIsUploading(false);
        break;
      case CSVUploadStatus.IN_PROGRESS:
        updateCurrentUploadStatus({
          status: CSVUploadStatus.IN_PROGRESS,
          progress: getUploadProgressPercentage(
            uploadStatus.total_records,
            uploadStatus.total_processed
          ),
        });
        break;

      case CSVUploadStatus.ERROR:
        getErrorDetails(
          CSVUploadStatus.ERROR,
          uploadStatus.error_count,
          uploadStatus.diners_created
        );
        setIsUploading(false);
        break;
      case CSVUploadStatus.FILE_ERROR:
        getErrorDetails(
          CSVUploadStatus.FILE_ERROR,
          uploadStatus.error_count,
          uploadStatus.diners_created
        );
        setIsUploading(false);
        break;
      case CSVUploadStatus.CANCELLED:
        clearUploadInterval();
        updateCurrentUploadStatus({
          status: CSVUploadStatus.CANCELLED,
          hasError: true,
        });
        setIsUploading(false);
        break;
      default: {
        updateCurrentUploadStatus({
          status: CSVUploadStatus.NEW,
        });
        break;
      }
    }
  }

  function getUploadProgressPercentage(
    totalRecords: number,
    totalProcessed: number
  ): number {
    // On CABS upload if there were diner deletions, total_record will not count them
    // at the end, it will affect the progress percentage calculation.
    // Taking into account that from the beginning of the process (for both type of accounts)
    // total_records starts > 0 and  total_processed = 0 when the following condition is met it is
    // because the processing has been completed
    if (totalRecords < totalProcessed) {
      return 100;
    }

    const currentProgress = Math.round((totalProcessed * 100) / totalRecords);

    if (totalRecords === 0 || currentProgress === 0) {
      return defaultUploadStatus.progress;
    }

    return currentProgress;
  }

  async function getErrorDetails(
    errorStatus: CSVUploadStatus.ERROR | CSVUploadStatus.FILE_ERROR,
    errorCount: number,
    totalRecords: number
  ) {
    clearUploadInterval();
    uploadPendingRequest.current = false;

    try {
      const { errors } = await sdk.http.get(
        `/corp-admin/${corpId}/diners/upload/${uploadRequestId.current}/details`
      );

      const formattedErrors: UploadError[] = errors.map(
        ({ id, line_number, property, message_key }) => {
          const errorMessage =
            CSV_UPLOAD_ERROR_MESSAGES[
              `${property}.${message_key}`.toLowerCase()
            ] || '';

          const uploadError: UploadError = {
            id,
            message: errorMessage,
          };

          if (errorStatus === CSVUploadStatus.ERROR) {
            uploadError.line = line_number;
          }

          return uploadError;
        }
      );
      const uploadStatus: Partial<UploadStatus> = {
        status: errorStatus,
        progress: 100,
        hasError: true,
        totalRecords,
      };

      if (errorStatus === CSVUploadStatus.FILE_ERROR) {
        uploadStatus.fileErrors = formattedErrors;
      } else {
        uploadStatus.lineErrors = formattedErrors;

        if (errorCount > MAX_POSSIBLE_ERRORS) {
          uploadStatus.exceedsMaxErrors = true;
        }
      }

      updateCurrentUploadStatus(uploadStatus);
    } catch (error) {
      showGeneralErrorMessage(error);
      updateCurrentUploadStatus({
        status: errorStatus,
        hasError: true,
      });
    }
  }

  return { currentUploadStatus, reset, startCsvUpload, isUploading };
};

export function getUniqueAndSortedErrors(
  errorArray: UploadError[] = []
): string[] {
  return uniqBy(errorArray, 'message')
    .map((error) => error.message)
    .sort();
}

export function getUploadingProgressYValue(
  shouldUploadAnotherCsv: boolean,
  totalRecords: number
): StackSpace {
  if (totalRecords && totalRecords > 0) {
    return 'xx-sm';
  }

  return shouldUploadAnotherCsv ? 'x-sm' : 'none';
}

export const getErrorMessage = (
  areKnownErrorsExist: boolean,
  exceedsMaxErrors: boolean
): string => {
  if (exceedsMaxErrors) {
    return `More than ${MAX_POSSIBLE_ERRORS} employee updates failed`;
  }

  if (areKnownErrorsExist) {
    return 'Couldn’t upload. Please correct the errors below, and try again.';
  }

  return 'Couldn’t upload.';
};

export const getErrorDetails = (
  areKnownErrorsExist: boolean,
  exceedsMaxErrors: boolean
): string => {
  if (exceedsMaxErrors) {
    return `The maximum number of errors was reached. Find the following errors in the CSV, correct them, and upload the same CSV again.`;
  }

  if (areKnownErrorsExist) {
    return 'There was a problem uploading your file. Review the errors below and try again.';
  }

  return 'There was a problem uploading your file.';
};
